// ===================================
// ADMIN LAYOUT COMPONENT
// src/components/layout/AdminLayout.js
// Wrapper for admin pages
// ===================================

import React, { useState } from 'react';
import { Outlet, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AdminLayout.css';

const AdminLayout = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-content">
          <div className="loading-logo">⚙️</div>
          <div className="loading-spinner"></div>
          <p>Loading admin panel...</p>
        </div>
      </div>
    );
  }

  // Redirect to admin login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Redirect to app if not admin
  if (!user?.is_admin) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => {
    return location.pathname === path || 
           (path !== '/admin/dashboard' && location.pathname.startsWith(path));
  };

  const adminNavItems = [
    { path: '/admin/dashboard', icon: '📊', label: 'Overview' },
    { path: '/admin/users', icon: '👥', label: 'Users' },
    { path: '/admin/sessions', icon: '📋', label: 'Sessions' },
    { path: '/admin/feedback', icon: '💬', label: 'Feedback' },  // <-- NEW STAGE 5
    { path: '/admin/coupons', icon: '🎟️', label: 'Coupons' },
    { path: '/admin/categories', icon: '📁', label: 'Categories' },
    { path: '/admin/prompts', icon: '📝', label: 'Prompts' },
    { path: '/admin/programs', icon: '🎓', label: 'Programs' },
    { path: '/admin/pricing', icon: '💰', label: 'Pricing' },
    { path: '/admin/analytics', icon: '📈', label: 'Analytics' },
    { path: '/admin/salesforce', icon: '☁️', label: 'Salesforce' },
    { path: '/admin/voice', icon: '🎙️', label: 'Voice' },
    { path: '/admin/errors', icon: '⚠️', label: 'Error Logs' },
    { path: '/admin/settings', icon: '⚙️', label: 'Settings' },
  ];

  return (
    <div className="admin-layout">
      {/* Mobile Overlay */}
      <div 
        className={`admin-sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Admin Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <Link to="/admin/dashboard" className="admin-logo">
            <div className="admin-logo-icon">⚙️</div>
            <div className="admin-logo-text">
              <span className="admin-title">Career-Coach.AI</span>
              <span className="admin-subtitle">Admin Panel</span>
            </div>
          </Link>
        </div>

        <nav className="admin-nav">
          <ul className="admin-nav-list">
            {adminNavItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`admin-nav-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="admin-nav-icon">{item.icon}</span>
                  <span className="admin-nav-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/app/dashboard" className="switch-to-app-btn">
            <span>🏠</span>
            <span>Switch to App</span>
          </Link>
          
          <div className="admin-user-section">
            <div className="admin-user-avatar">
              {user?.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div className="admin-user-info">
              <span className="admin-user-name">{user?.name || user?.username}</span>
              <span className="admin-user-role">Administrator</span>
            </div>
            <button 
              className="admin-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              🚪
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-main">
        {/* Top Bar */}
        <header className="admin-topbar">
          <button 
            className="admin-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className="admin-topbar-title">
            <h1>Admin Dashboard</h1>
          </div>

          <div className="admin-topbar-actions">
            <Link to="/app/dashboard" className="admin-action-btn">
              View App →
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;